import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getMechanicJobs, updateMechanicJobStatus } from '../../api/mechanicAuth.api';
import '../../styles/mechanic.css';

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#2563eb',
  dispatched: '#7c3aed',
  'in-progress': '#0891b2',
  completed: '#16a34a',
  cancelled: '#dc2626',
};

const StatusBadge = ({ status }) => (
  <span className="mechanic-status-badge" style={{ background: STATUS_COLORS[status] + '20', color: STATUS_COLORS[status] }}>
    {status?.replace('-', ' ')}
  </span>
);

const STATUS_FILTERS = ['all', 'pending', 'confirmed', 'dispatched', 'in-progress', 'completed', 'cancelled'];

const MechanicJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const loadJobs = useCallback(() => {
    setLoading(true);
    const params = filter !== 'all' ? { status: filter } : {};
    getMechanicJobs({ ...params, limit: 50 })
      .then((d) => setJobs(d.requests || []))
      .catch(() => setError('Failed to load jobs.'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleUpdateStatus = async (jobId, newStatus) => {
    setUpdating(jobId);
    setError('');
    try {
      await updateMechanicJobStatus(jobId, { status: newStatus, notes: statusNote });
      setSuccessMsg('Status updated successfully.');
      setExpanded(null);
      setStatusNote('');
      loadJobs();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="mechanic-page">
      <div className="mechanic-container">
        <div className="mechanic-header">
          <div>
            <h1 className="mechanic-title">My Jobs</h1>
            <p className="mechanic-subtitle">Manage your assigned service requests</p>
          </div>
          <Link to="/mechanic/dashboard" className="mechanic-back-btn">← Dashboard</Link>
        </div>

        {successMsg && <div className="auth-success-box" style={{ marginBottom: '1rem' }}>{successMsg}</div>}
        {error && <div className="auth-error-box" style={{ marginBottom: '1rem' }}>{error}</div>}

        {/* Status Filter */}
        <div className="mechanic-filter-row">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              className={`mechanic-filter-btn ${filter === s ? 'active' : ''}`}
              onClick={() => setFilter(s)}
            >
              {s === 'all' ? 'All' : s.replace('-', ' ')}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mechanic-loading"><div className="auth-spinner" /></div>
        ) : jobs.length === 0 ? (
          <div className="mechanic-empty"><p>No jobs found.</p></div>
        ) : (
          <div className="mechanic-job-list">
            {jobs.map((job) => (
              <div key={job.id} className={`mechanic-job-card ${expanded === job.id ? 'expanded' : ''}`}>
                <div
                  className="mechanic-job-main"
                  onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="mechanic-job-info">
                    <div className="mechanic-job-tracking">{job.trackingId}</div>
                    <div className="mechanic-job-customer">{job.customerName} — {job.customerPhone}</div>
                    <div className="mechanic-job-service">{job.serviceName || job.service?.name || 'Roadside Service'}</div>
                    {job.brand && <div className="mechanic-job-vehicle">{job.brand.name} {job.model?.name} {job.vehicleType ? `(${job.vehicleType})` : ''}</div>}
                    {job.locationAddress && (
                      <div className="mechanic-job-location">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {job.locationAddress}
                      </div>
                    )}
                    <div className="mechanic-job-date">
                      {new Date(job.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                    <StatusBadge status={job.status} />
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{expanded === job.id ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expanded === job.id && !['completed', 'cancelled'].includes(job.status) && (
                  <div className="mechanic-job-actions">
                    <p style={{ fontSize: '0.8125rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Update Status</p>
                    <textarea
                      className="mechanic-notes-input"
                      placeholder="Add notes (optional)"
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)}
                      rows={2}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {job.status !== 'in-progress' && (
                        <button
                          className="mechanic-action-btn primary"
                          disabled={updating === job.id}
                          onClick={() => handleUpdateStatus(job.id, 'in-progress')}
                        >
                          {updating === job.id ? 'Updating...' : 'Mark In-Progress'}
                        </button>
                      )}
                      <button
                        className="mechanic-action-btn success"
                        disabled={updating === job.id}
                        onClick={() => handleUpdateStatus(job.id, 'completed')}
                      >
                        {updating === job.id ? 'Updating...' : 'Mark Completed'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicJobs;
